import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { createDemandClient } from '../../agency/demandApi';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createCreativesClient } from './api';
import type { CreativeMutationPayload, CreativesFilter } from './model';

export const creativesQueryKey = ['campaigns', 'creatives'] as const;
export const creativesListsQueryKey = [...creativesQueryKey, 'list'] as const;

function useCreativesClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const scope = useAgencyCampaignContext();
  if (!session) throw new Error('Shigo session is required.');
  if (scope)
    return createDemandClient({
      config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session.accessToken,
      agencyId: scope.agency.id,
    }).creatives;
  return createCreativesClient({
    config: { apiBaseUrl: config.madBuyServiceUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useCreativesQuery(filter: CreativesFilter) {
  const client = useCreativesClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...creativesListsQueryKey, filter],
    queryFn: filter.agencyIds?.length ? skipToken : () => client.list(filter),
    enabled: Boolean(filter.brandId) && !filter.agencyIds?.length,
  });
}

export function useCreativeQuery(brandId: string, creativeId: string | undefined) {
  const client = useCreativesClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  return useQuery({
    queryKey: [...prefix, ...creativesQueryKey, 'detail', brandId, creativeId],
    queryFn: () => client.detail(brandId, creativeId ?? ''),
    enabled: Boolean(brandId && creativeId),
    refetchOnMount: 'always',
  });
}

export function useCreativeMutations() {
  const client = useCreativesClient();
  const scope = useAgencyCampaignContext();
  const prefix = scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : [];
  const queryClient = useQueryClient();
  const invalidateLists = () => {
    const refresh = queryClient.invalidateQueries({
      queryKey: [...prefix, ...creativesListsQueryKey],
    });
    if (!scope) return refresh;
    void refresh;
    void queryClient.invalidateQueries({ queryKey: [...prefix, 'mad-buy-creatives'] });
  };
  return {
    create: useMutation({
      mutationFn: ({
        brandId,
        payload,
        file,
      }: {
        brandId: string;
        payload: CreativeMutationPayload;
        file?: File | null;
      }) => client.create(brandId, payload, file),
      onSuccess: invalidateLists,
    }),
    update: useMutation({
      mutationFn: ({
        brandId,
        creativeId,
        payload,
        file,
      }: {
        brandId: string;
        creativeId: string;
        payload: CreativeMutationPayload;
        file?: File | null;
      }) => client.update(brandId, creativeId, payload, file),
      onSuccess: invalidateLists,
    }),
    duplicate: useMutation({
      mutationFn: ({ brandId, creativeId }: { brandId: string; creativeId: string }) =>
        client.duplicate(brandId, creativeId),
      onSuccess: invalidateLists,
    }),
  };
}
